import { Group } from "three";
import type { Scene } from "three";
import type { Entity } from "../types/entity";

/**
 * Contenu du jeu. Construit après l'événement `ready` du Loader : les
 * constructeurs d'entités sont donc synchrones, aucun await ici.
 */
export class World {
  readonly root = new Group();

  private readonly entities: Entity[] = [];

  constructor(private readonly scene: Scene) {
    this.root.name = "world";
    scene.add(this.root);
  }

  add(entity: Entity): void {
    this.entities.push(entity);
    this.root.add(entity.object3D);
  }

  /** Retire l'entité du monde et la libère : le monde en est propriétaire. */
  remove(entity: Entity): void {
    const index = this.entities.indexOf(entity);
    if (index === -1) return;
    this.entities.splice(index, 1);
    this.root.remove(entity.object3D);
    entity.dispose();
  }

  /**
   * Remplace une entité par sa nouvelle version et renvoie celle-ci. Retirer
   * puis ajouter est le geste courant d'une régénération de carte : le faire
   * en un appel évite d'oublier la libération de l'ancienne.
   */
  replace<T extends Entity>(previous: T | null, next: T): T {
    if (previous) this.remove(previous);
    this.add(next);
    return next;
  }

  update(dt: number): void {
    for (const entity of this.entities) entity.update?.(dt);
  }

  dispose(): void {
    for (const entity of this.entities) {
      this.root.remove(entity.object3D);
      entity.dispose();
    }
    this.entities.length = 0;
    this.scene.remove(this.root);
  }
}
